import * as uploadPolicy from '@theme/pb-upload-policy';
/** Product Builder shared upload boundary: validate, prepare, then store once. */
(function () {
  'use strict';
  function policy() { return Promise.resolve(uploadPolicy); }
  function getMediaApiEndpoint() {
    return String((window.BUILDER_DATA || {}).mediaApiEndpoint || '').trim().replace(/\/$/, '');
  }
  function inferKind(file) {
    var type = String(file.type || '');
    if (type.indexOf('audio/') === 0) return 'audio';
    if (type.indexOf('video/') === 0) return 'video';
    return 'photo';
  }
  function preparePhoto(file, options, rules, type) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      var settled = false;
      var timer = setTimeout(function () { finish(new Error('This photo could not be read in time. Please choose a JPG, PNG or WebP.')); }, 15000);
      function finish(error, result) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        URL.revokeObjectURL(url);
        img.onload = img.onerror = null;
        if (error) reject(error); else resolve(result);
      }
      img.onerror = function () { finish(new Error('This photo is damaged or its format is not supported by your browser. Save it as JPG or PNG and try again.')); };
      img.onload = function () {
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          if (!w || !h || w * h > rules.MAX_PHOTO_PIXELS || w > 16384 || h > 16384) {
            finish(new Error('Photo dimensions are too large or unreadable. Use a photo up to 40 megapixels and 16,384 pixels per side.'));
            return;
          }
          var heic = type === 'image/heic' || type === 'image/heif';
          var scale = options.ai ? Math.min(1, rules.AI_MAX_EDGE / Math.max(w, h)) : 1;
          if (!heic && scale === 1 && (!options.ai || file.size <= rules.AI_MAX_BYTES)) {
            finish(null, file.type ? file : new File([file], file.name, {type:type}));
            return;
          }
          var canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(w * scale));
          canvas.height = Math.max(1, Math.round(h * scale));
          var ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Photo preparation is unavailable. Please try another browser.');
          // AI derivatives and HEIC conversions use a white background; normal PNG/WebP stays original.
          ctx.fillStyle = '#ffffff'; ctx.fillRect(0,0,canvas.width,canvas.height);
          ctx.drawImage(img,0,0,canvas.width,canvas.height);
          canvas.toBlob(function (blob) {
            canvas.width = canvas.height = 0;
            if (!blob) { finish(new Error('Could not prepare this photo. Please save it as JPG and try again.')); return; }
            var name = String(file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
            finish(null,new File([blob],name,{type:'image/jpeg'}));
          }, 'image/jpeg', options.ai ? 0.88 : 0.94);
        } catch (error) { finish(error); }
      };
      img.src = url;
    });
  }
  function prepareUploadFile(file, options) {
    return policy().then(function (rules) {
      var kind = options.kind || inferKind(file);
      var type = rules.validateUpload(file, {kind:kind,maxMb:options.maxMb});
      var prepared = kind === 'photo' ? preparePhoto(file,options,rules,type) : Promise.resolve(file);
      return prepared.then(function (result) {
        rules.validateUpload(result,{kind:kind,maxMb:options.ai ? Math.min(options.maxMb || 20,5) : options.maxMb,prepared:true});
        return result;
      });
    });
  }
  function upload(file, options) {
    options = options || {};
    var api = getMediaApiEndpoint();
    if (!api) return Promise.reject(new Error('Upload service is unavailable. Please try again later.'));
    return prepareUploadFile(file, options).then(function (prepared) {
      var kind = options.kind || inferKind(prepared);
      var name = prepared.name || (kind === 'audio' ? 'recording.webm' : kind === 'video' ? 'video.mp4' : 'photo.jpg');
      var form = new FormData();
      form.append('file', prepared, name);
      form.append('kind',kind);
      if (options.label) form.append('label',options.label);
      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); },120000);
      // Do not retry writes automatically: an interrupted response may already have stored the file.
      return fetch(api + '/upload',{method:'POST',body:form,signal:controller.signal}).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (json) {
          if (!res.ok) throw new Error(json.error || (res.status === 413 ? 'This file is too large. Please choose a smaller file.' : 'Upload service is temporarily unavailable. Please try again.'));
          var url = json.url || json.secure_url || json.publicUrl;
          if (!url) throw new Error('The upload did not finish. Please try again.');
          return url;
        });
      }).catch(function (error) {
        if (error.name === 'AbortError') throw new Error('Upload timed out. Check your connection and try again.');
        if (error instanceof TypeError) throw new Error('Connection lost during upload. Check your connection and try again.');
        throw error;
      }).finally(function () { clearTimeout(timer); });
    });
  }
  window.pbMediaUpload = {
    getMediaApiEndpoint:getMediaApiEndpoint,
    usesR2PresignedUpload:function () { return !!getMediaApiEndpoint(); },
    prepareUploadFile:prepareUploadFile,
    upload:upload,
  };
})();
